import {
  BarChart3,
  ClipboardList,
  History,
  LogOut,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShoppingBag,
  UtensilsCrossed,
  UserRoundCog,
  X,
} from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "#auth.tsx";
import { useOrders } from "#orders-context.tsx";
import { OrderClosureToast } from "#order-closure-toast.tsx";
import { AccountDevicesDialog } from "#account-devices-dialog.tsx";
import { useLanguage } from "#i18n.tsx";

const links = [
  {
    to: "/admin/kitchen",
    title: "nav.orders" as const,
    icon: ShoppingBag,
    admin: false,
  },
  {
    to: "/admin/menu",
    title: "nav.menu" as const,
    icon: UtensilsCrossed,
    admin: false,
  },
  {
    to: "/admin/preorders",
    title: "nav.preorders" as const,
    icon: ClipboardList,
    admin: true,
  },
  { to: "/admin/pos", title: "nav.history" as const, icon: History, admin: true },
  { to: "/admin/dashboard", title: "nav.dashboard" as const, icon: BarChart3, admin: true },
  { to: "/orders", title: "nav.customerDisplay" as const, icon: Monitor, admin: true },
  { to: "/settings", title: "nav.settings" as const, icon: Settings, admin: true },
];

/** Collapsible staff navigation, with the current Pocket Rice artwork. */
export function AppShell() {
  const { logout, state } = useAuth();
  const { t } = useLanguage();
  const { connection, closure } = useOrders();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("landpad.sidebar") === "collapsed",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const navigation = useRef<HTMLElement>(null);
  useEffect(() => {
    localStorage.setItem(
      "landpad.sidebar",
      collapsed ? "collapsed" : "expanded",
    );
  }, [collapsed]);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen || !navigation.current) return;
    const panel = navigation.current;
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const controls = () =>
      [...panel.querySelectorAll<HTMLElement>("button, a[href]")].filter(
        (element) => element.getClientRects().length > 0,
      );
    controls()[0]?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = controls();
      const first = items[0];
      const last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    panel.addEventListener("keydown", trap);
    return () => {
      panel.removeEventListener("keydown", trap);
      document.body.style.overflow = previousOverflow;
      if (previous instanceof HTMLElement)
        previous.focus({ preventScroll: true });
    };
  }, [mobileOpen]);
  useEffect(() => {
    const keyboard = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "b") {
        event.preventDefault();
        setCollapsed((value) => !value);
      }
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, []);
  return (
    <div
      className={`app-shell ${collapsed ? "sidebar-collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}
    >
      <a className="skip-link" href="#main-content">
        {t("nav.skip")}
      </a>
      {mobileOpen && (
        <button
          className="sidebar-backdrop"
          aria-label={t("nav.close")}
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        ref={navigation}
        className="sidebar"
        id="staff-navigation"
        role={mobileOpen ? "dialog" : undefined}
        aria-modal={mobileOpen || undefined}
        aria-label={t("nav.staff")}
      >
        <div className="brand">
          <img className="brand-logo" src="/title-icon.png" alt="Pocket Rice" />
          <div className="nav-label">
            <strong>Pocket Rice</strong>
            <span>Landpad</span>
          </div>
          <button
            className="icon-button mobile-close"
            aria-label={t("nav.close")}
            onClick={() => setMobileOpen(false)}
          >
            <X />
          </button>
        </div>
        <nav aria-label={t("nav.operations")}>
          {links
            .filter(
              ({ admin }) =>
                !admin ||
                (state.kind === "authenticated" && state.role === "admin"),
            )
            .map(({ to, title, icon: Icon }) => (
              <NavLink key={to} to={to} title={t(title)}>
                <Icon aria-hidden="true" />
                <span className="nav-label">{t(title)}</span>
              </NavLink>
            ))}
        </nav>
        <div className="sidebar-footer">
          <button
            className="logout desktop-toggle"
            title={collapsed ? t("nav.expand") : t("nav.collapse")}
            aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            <span className="nav-label">{t("nav.collapse")}</span>
          </button>
          <span className={`connection ${connection}`} role="status">
            <span className="connection-dot" />
            <span className="nav-label">
              {connection === "live"
                ? t("nav.live")
                : connection === "offline"
                  ? t("nav.offline")
                  : t("nav.connecting")}
            </span>
          </span>
          <button
            className="logout"
            title={t("nav.account")}
            onClick={() => setAccountOpen(true)}
          >
            <UserRoundCog aria-hidden="true" />
            <span className="nav-label">{t("nav.account")}</span>
          </button>
          <button
            className="logout"
            title={t("nav.logout")}
            onClick={() => void logout()}
          >
            <LogOut aria-hidden="true" />
            <span className="nav-label">{t("nav.logout")}</span>
          </button>
        </div>
      </aside>
      <div className="workspace-shell" inert={mobileOpen}>
        <button
          className="icon-button mobile-toggle"
          aria-label={t("nav.open")}
          aria-expanded={mobileOpen}
          aria-controls="staff-navigation"
          onClick={() => setMobileOpen(true)}
        >
          <PanelLeftOpen />
        </button>
        <main id="main-content" className="workspace">
          <Outlet />
        </main>
        {closure && (
          <OrderClosureToast key={closure.revision} change={closure} />
        )}
      </div>
      {accountOpen && (
        <AccountDevicesDialog close={() => setAccountOpen(false)} />
      )}
    </div>
  );
}
